import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import { AuthProvider } from '@shared/auth/AuthContext'
import { TrialExpirationProvider } from '@shared/contexts/TrialExpirationContext'
import { PublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import { msalConfig, setPendingMsalRedirectResult } from './config/msalConfig'

// Suppress noisy browser warnings that don't affect functionality
const originalWarn = console.warn
const originalError = console.error

console.warn = (...args: any[]) => {
  const message = args.join(' ')

  // Suppress Stripe tracking prevention warnings (browser privacy features blocking Stripe's fraud detection)
  if (message.includes('Tracking Prevention') && message.includes('stripe')) {
    return
  }

  originalWarn.apply(console, args)
}

console.error = (...args: any[]) => {
  const message = args.join(' ')

  // Suppress Stripe tracking prevention errors (browser privacy features blocking Stripe iframes)
  if (message.includes('Tracking Prevention') && (message.includes('stripe') || message.includes('m.stripe.network'))) {
    return
  }

  originalError.apply(console, args)
}

// MSAL v3 requires initialize() to be awaited before any MSAL API is called.
// We pre-initialize before rendering so that components (e.g. MicrosoftCallbackPage)
// can safely call handleRedirectPromise() in a useEffect without hitting
// "uninitialized_public_client_application" errors.
async function initializeApp() {
  const msalInstance = new PublicClientApplication(msalConfig)
  await msalInstance.initialize()

  // Capture the redirect result immediately while the MSAL promise cache is populated.
  // handleRedirectPromise() is called here (same execution context as initialize()),
  // so it returns the already-resolved result. Storing it in a module variable lets
  // MicrosoftCallbackPage consume it reliably without re-calling the API.
  const redirectResult = await msalInstance.handleRedirectPromise()
  setPendingMsalRedirectResult(redirectResult)

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <AuthProvider>
          <TrialExpirationProvider>
            <App />
          </TrialExpirationProvider>
        </AuthProvider>
      </MsalProvider>
    </React.StrictMode>
  )
}

initializeApp()